import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * REFUSED DELIVERIES — a paid order must never be invisible.
 *
 * On 21 Sep 2026 a real $29 order (receipt REC10003, Joel Murillo) was lost
 * because the CRM workflow posted to payment-webhook with a WRONG 26-character
 * x-webhook-secret. The endpoint refused it — correctly — but the body was
 * dropped, so nothing on the platform knew a customer had paid.
 *
 * Every refusal is now stored in mkt_webhook_quarantine with its full payload.
 * This panel shows them, explains the cause in plain language, and lets an
 * admin grant the credit the buyer already paid for. The grant is idempotent
 * and tagged, so if the CRM later delivers the real transaction the webhook
 * reconciles it instead of crediting twice.
 */

type Row = {
  id: string;
  reason: string;
  event_id: string | null;
  event_type: string | null;
  plan_code: string | null;
  email: string | null;
  amount: number;
  headers_present: string[] | null;
  source_ip: string | null;
  user_agent: string | null;
  payload: Record<string, unknown> | null;
  replayed: boolean;
  replay_result: string | null;
  admin_note: string | null;
  created_at: string;
};

const money = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;

const REASON_TEXT: Record<string, string> = {
  secret_mismatch:
    'The sender posted an x-webhook-secret that does not match the live shared secret. Copy the secret again — no spaces, no quotes, no line break.',
  secret_absent:
    'The sender posted no x-webhook-secret header at all. Add it to the workflow webhook action as a custom header.',
  secret_variable_name_sent:
    'The sender posted the literal text "GHL_PAYMENT_WEBHOOK_SECRET" — that is a variable NAME, not a value. Paste the 48-character secret itself into the header value field.'
};


const AdminWebhookQuarantine: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('mkt_webhook_quarantine')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(25);
    setRows((data as Row[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grant = async (row: Row) => {
    if (!row.email) {
      setMessage('That delivery carried no email address, so it cannot be matched to an account.');
      return;
    }
    setBusy(row.id);
    setMessage(null);
    const receipt = (row.event_id || row.id).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40);
    const { data, error } = await supabase.functions.invoke('single-lead-recovery', {
      body: { email: row.email, receipt, amount: row.amount || 2900 }
    });
    setBusy(null);
    if (error || data?.ok === false) {
      setMessage(`Could not grant the credit: ${data?.error || error?.message || 'unknown error'}`);
      return;
    }
    if (data?.duplicate) {
      setMessage(`${row.email} was already credited for this payment — nothing was granted twice.`);
    } else {
      setMessage(
        `Granted 1 lead credit to ${row.email}. Balance is now ${data?.spendableTotal ?? 1} credit(s). Ledger entry ${data?.ledgerEntryId || 'written'}.`
      );
    }
    await supabase
      .from('mkt_webhook_quarantine')
      .update({ admin_note: 'credit granted by hand from quarantine', replayed: true, replayed_at: new Date().toISOString() })
      .eq('id', row.id);
    load();
  };

  const openRows = rows.filter((r) => !r.replayed);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Refused payment deliveries</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Deliveries the platform turned away because the shared secret was missing or wrong. The full payload is kept,
            so a customer who really paid can still be credited. Empty is the healthy state.
          </p>
        </div>
        <button
          onClick={load}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>

      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">How to stop this happening again</p>
        <p className="mt-1">
          In every GoHighLevel payment workflow, the webhook action must send a custom header named
          <code className="mx-1 rounded bg-white px-1 py-0.5 text-xs">x-webhook-secret</code>
          whose value is the live shared secret — <strong>48 characters</strong>. A value of any other length is the
          wrong string. Paste it with no surrounding spaces, quotes or braces.
        </p>
        <p className="mt-2">
          Do <strong>not</strong> paste the text <code className="rounded bg-white px-1 py-0.5 text-xs">GHL_PAYMENT_WEBHOOK_SECRET</code>.
          That is the variable name, not its value, and it is what caused the lost order of 21 Sep 2026. Deliveries
          carrying it are refused and logged as <em>secret_variable_name_sent</em>.
        </p>
      </div>


      {message && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">{message}</div>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          No refused deliveries recorded. Every payment notification that arrived was accepted.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-600">
            {openRows.length} awaiting action · {rows.length - openRows.length} resolved
          </p>
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    r.replayed ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                  }`}
                >
                  {r.replayed ? 'Resolved' : 'Needs action'}
                </span>
                <span className="text-sm font-semibold text-slate-900">{r.email || 'no email in payload'}</span>
                <span className="text-sm text-slate-600">
                  {r.plan_code || 'unknown product'} · {money(r.amount)}
                </span>
                <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
              </div>

              <p className="mt-2 text-sm text-slate-700">{REASON_TEXT[r.reason] || r.reason}</p>

              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                <span>Transaction: {r.event_id || '—'}</span>
                <span>Sender: {r.source_ip || '—'}</span>
                <span>Agent: {r.user_agent || '—'}</span>
                <span>Headers seen: {(r.headers_present || []).join(', ') || '—'}</span>
                {r.replay_result && <span>Result: {r.replay_result}</span>}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {!r.replayed && r.plan_code === 'single' && (
                  <button
                    onClick={() => grant(r)}
                    disabled={busy === r.id}
                    className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {busy === r.id ? 'Granting…' : 'Grant the paid lead credit'}
                  </button>
                )}
                <button
                  onClick={() => setOpen(open === r.id ? null : r.id)}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {open === r.id ? 'Hide payload' : 'View payload'}
                </button>
              </div>

              {open === r.id && (
                <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
                  {JSON.stringify(r.payload || {}, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default AdminWebhookQuarantine;
